import type { AuthRole } from '..';

export type InfoField =
  | 'bookTitle'
  | 'code'
  | 'schoolName'
  | 'schoolAddress'
  | 'question'
  // A complete form to request a license code, if applicable
  | 'requestLicenseCodeForm'
  // No extra fields, render only a specific error message
  | 'errorContactTeacher'
  | 'errorNotLoggedIn';

const infoFields: Record<string, InfoField[]> = {
  question: ['question'],
  default: ['question', 'bookTitle', 'code'],
  noCode: ['question', 'bookTitle'],
  school: ['question', 'schoolName', 'schoolAddress'],
};

const BASE = 'PAGES.HELP.SECTIONS.CONTACT.SUBJECT.OPTIONS';

const commonSubjects: Subject[] = [
  {
    name: 'NoActivationCode',
    translation: `${BASE}.NO_ACTIVATION_CODE`,
    fields: {
      ANONYMOUS: ['errorContactTeacher'],
      STUDENT: ['errorContactTeacher'],
      TEACHER: infoFields.noCode,
    },
  },
  {
    name: 'ActivationCodeNotAccepted',
    translation: `${BASE}.ACTIVATION_CODE_NOT_ACCEPTED`,
    fields: {
      ANONYMOUS: infoFields.default,
      STUDENT: infoFields.default,
      TEACHER: infoFields.default,
    },
  },
  {
    name: 'MaterialIssue',
    translation: `${BASE}.MATERIAL_ISSUE`,
    fields: {
      ANONYMOUS: infoFields.default,
      STUDENT: infoFields.default,
      TEACHER: infoFields.default,
    },
  },
  {
    name: 'Question',
    translation: `${BASE}.QUESTION`,
    fields: {
      ANONYMOUS: infoFields.default,
      STUDENT: infoFields.default,
      TEACHER: infoFields.default,
    },
  },
  {
    name: 'SchoolNotFound',
    translation: `${BASE}.SCHOOL_NOT_FOUND`,
    fields: {
      ANONYMOUS: infoFields.school,
      STUDENT: infoFields.school,
      TEACHER: infoFields.school,
    },
  },
];

const anonymousSubject: Subject = {
  name: 'CannotLogin',
  translation: `${BASE}.CANNOT_LOGIN`,
  fields: {
    ANONYMOUS: infoFields.question,
    STUDENT: [],
    TEACHER: [],
  },
};

const studentHasNoCode: Subject = {
  name: 'StudentHasNoCode',
  translation: `${BASE}.STUDENT_HAS_NO_CODE`,
  fields: {
    ANONYMOUS: ['errorNotLoggedIn'],
    STUDENT: ['errorNotLoggedIn'],
    TEACHER: ['requestLicenseCodeForm'],
  },
};

export interface Subject {
  name: string;
  translation: string;
  fields: Record<AuthRole, InfoField[]>;
}

export interface useSubjectsProps {
  isAnonymous: boolean;
  isSecondaryTeacher: boolean;
}

export default function useSubjects({ isAnonymous, isSecondaryTeacher }: useSubjectsProps) {
  const getSubjects = () => {
    if (isAnonymous) {
      return [anonymousSubject, studentHasNoCode, ...commonSubjects];
    }
    if (isSecondaryTeacher) {
      return [studentHasNoCode, ...commonSubjects];
    }
    return commonSubjects;
  };

  return {
    getSubjects,
  };
}
